import { Component, OnInit, Input, EventEmitter, Output, ChangeDetectorRef, inject } from '@angular/core';
import { HardwareVariant } from '../../../../models/mobilephone-variant';
import { WarehouseStateModel } from '@sales/states/warehouse/warehouse.state';
import { ControlContainer, FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { select, Store } from '@ngxs/store';
import { take } from 'rxjs';
import { NotificationService } from '@core/services/notification.service';
import { MobilePhoneFormControls } from '../../../mobilephone-search/mobilephone-details/mobilephone-details.component';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Regex } from '@shared/enums/regex';
import { DuplicateImeiInShoppingCartValidator, ImeiValidator } from '@sales/validators/imei-validator';
import { ApiGetAllSubscriptionOfferingsAction } from '@sales/states/customer-product/customer-product.actions';
import { GtinValidator } from '@app/sales/validators/gtin-validator';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { HttpStatusCode } from '@angular/common/http';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { MobilephoneDeliveryTypeComponent } from '../../../mobilephone-search/mobilephone-details/mobilephone-delivery-type/mobilephone-delivery-type.component';
import { MobilephoneInsuranceOptionsComponent } from '../../../mobilephone-search/mobilephone-details/mobilephone-insurance-options/mobilephone-insurance-options.component';
import { EquipmentVoiceNumberTypeComponent } from '../../../../../equipment/equipment-dialog/equipment-forms/equipment-voice-number-type/equipment-voice-number-type.component';
import { MobilephonePaymentDetailsComponent } from '../../../mobilephone-search/mobilephone-details/mobilephone-payment-details/mobilephone-payment-details.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { MobilephoneVariantOptionsComponent } from '../../../mobilephone-search/mobilephone-details/mobilephone-variant-options/mobilephone-variant-options.component';
import { MobilephoneGtinScanComponent } from '../../../mobilephone-search/mobilephone-details/mobilephone-gtin-scan/mobilephone-gtin-scan.component';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';
import { HardwareSearchService } from '@app/sales/modules/accessories/services/hardware-search.service';

enum Messages {
  ErrorGettingMobileDetails = 'Feil ved henting av telefondetaljer',
}

@Component({
  selector: 'app-new-mobile-phone-details',
  templateUrl: './new-mobile-phone-details.component.html',
  styleUrls: ['./new-mobile-phone-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceCardComponent,
    MobilephoneGtinScanComponent,
    MobilephoneVariantOptionsComponent,
    SpinnerComponent,
    MobilephonePaymentDetailsComponent,
    EquipmentVoiceNumberTypeComponent,
    MobilephoneInsuranceOptionsComponent,
    MobilephoneDeliveryTypeComponent,
    IceButtonComponent,
  ],
})
export class NewMobilePhoneDetailsComponent implements OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);
  private notificationService = inject(NotificationService);
  private hardwareSearchService = inject(HardwareSearchService);
  private formGroupDirective = inject(FormGroupDirective);

  MobilePhoneFormControls = MobilePhoneFormControls;

  @Input() selectedExistingSubscriptionId: string;
  @Input() mobilePhoneVariant: HardwareVariant;
  @Input() mobilePhoneGroupVariants: HardwareVariant[];
  @Input() warehouseState: WarehouseStateModel;
  @Input() isEquipmentUpgradeEnabled: boolean;
  @Input() hasPerformedDetailedCreditCheck: boolean;
  @Input() isEquipmentUpgradePreCheckSuccessful: boolean;
  @Input() canSellHardwareWithLogisticsClaim: boolean;
  @Input() existingVoiceProducts: VoiceProduct[];
  @Input() hasSubscriptionsAvailableForRedemption: boolean;
  @Input() existingProductsAvailableForEquipmentUpgradeUpgrade: VoiceProduct[];
  @Input() isSubscriptionLimitReached: boolean;
  @Input() isManualRegistration: boolean;
  @Input({ required: false }) isMobilBytteBytte = false;

  @Output() selectionCompleted = new EventEmitter();
  @Output() setNewMobilePhoneOffering = new EventEmitter();
  @Output() setNewMobilePhoneVariant = new EventEmitter();
  @Output() backToSearch = new EventEmitter();

  canSellMobileInsurance = select(DealerStateSelectors.canSellMobileInsurance);
  allVoiceProducts = select(CustomerProductSelectors.getAllVoiceProducts);
  imeisInShoppingCart = select(ShoppingCartSelectors.imeisInShoppingCart);
  canUpdateImei = select(DealerStateSelectors.canUpdateImei);

  newMobileForm: FormGroup;
  selectedVariant: HardwareVariant = null;
  isLoadingMobilePhoneOffering: boolean;
  newMobilePhoneVariant: HardwareVariant;
  ngOnInit() {
    this.newMobileForm = this.formGroupDirective.form;

    this.selectedVariant = this.mobilePhoneVariant;
    this.initializeForm();

    this.getMobileDetails();
  }

  private initializeForm() {
    if (Object.keys(this.newMobileForm.controls).length === 0) {
      if (this.isManualRegistration) {
        this.newMobileForm.addControl(
          MobilePhoneFormControls.Gtin,
          this.formBuilder.control(this.mobilePhoneVariant?.gtin, [Validators.required, GtinValidator()])
        );
      } else {
        this.newMobileForm.addControl(MobilePhoneFormControls.Color, this.formBuilder.control(null, [Validators.required]));
        this.newMobileForm.addControl(MobilePhoneFormControls.Memory, this.formBuilder.control(null, [Validators.required]));
      }

      this.newMobileForm.addControl(
        MobilePhoneFormControls.Imei,
        this.formBuilder.control(null, [
          Validators.required,
          Validators.minLength(15),
          Validators.maxLength(15),
          Validators.pattern(Regex.IMEI),
          ImeiValidator(),
        ])
      );
      if (this.canUpdateImei()) {
        this.newMobileForm
          .get(MobilePhoneFormControls.Imei)
          .addValidators([DuplicateImeiInShoppingCartValidator(this.imeisInShoppingCart())]);
      }

      this.newMobileForm.addControl(
        MobilePhoneFormControls.PaymentType,
        this.formBuilder.control(MobilePaymentType.EquipmentUpgrade, [Validators.required])
      );
      this.newMobileForm.addControl(
        MobilePhoneFormControls.NewOfferingId,
        this.formBuilder.control(null, [Validators.required])
      );
      this.newMobileForm.addControl(
        MobilePhoneFormControls.ExistingSubscriptionId,
        this.formBuilder.control(this.hasSubscriptionsAvailableForRedemption ? this.selectedExistingSubscriptionId : null)
      );
      this.newMobileForm.addControl(
        MobilePhoneFormControls.IsSubscriptionUpgrade,
        this.formBuilder.control(null, [Validators.required])
      );
      this.newMobileForm.addControl(
        MobilePhoneFormControls.SubscriptionIdToUpgrade,
        this.formBuilder.control(null, [Validators.required])
      );
      this.newMobileForm.addControl(
        MobilePhoneFormControls.IsSoldInShop,
        this.formBuilder.control(this.isManualRegistration, [Validators.required])
      );

      this.newMobileForm.addControl(MobilePhoneFormControls.MobilBytteOffering, this.formBuilder.control(null));

      this.newMobileForm.addControl(
        MobilePhoneFormControls.InsuranceOption,
        this.formBuilder.control(null, [Validators.required])
      );
    }
  }

  onGtinScanned(gtin: string) {
    this.isLoadingMobilePhoneOffering = true;
    const subscriptionIds = this.existingVoiceProducts.map(vp => vp.subscription?.id);
    this.hardwareSearchService
      .getHardwareWithGtin(gtin, subscriptionIds)
      .pipe(take(1))
      .subscribe({
        next: mobile => {
          const mobileHasEquipmentUpgrade = mobile.mobilbytteOfferings?.canHaveMobilbytte;
          if (!mobileHasEquipmentUpgrade) {
            this.notificationService.default('Fant ingen mobilbytte-kompatibel telefon for scannet EAN/GTIN');
          } else {
            this.mobilePhoneVariant = mobile;
            this.newMobilePhoneVariant = mobile;
            this.setNewMobilePhoneVariant.emit(mobile);
          }
          this.isLoadingMobilePhoneOffering = false;
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          if (error.status === HttpStatusCode.NotFound) {
            this.notificationService.default('Fant ingen mobiler med scannet EAN/GTIN');
          }
          this.isLoadingMobilePhoneOffering = false;
          this.mobilePhoneVariant = null;
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  private getMobileDetails() {
    this.isLoadingMobilePhoneOffering = true;

    if (!this.selectedVariant && !this.isManualRegistration) {
      this.notificationService.error(Messages.ErrorGettingMobileDetails);
      this.isLoadingMobilePhoneOffering = false;
    } else {
      this.isLoadingMobilePhoneOffering = false;
      this.mobilePhoneVariant = this.selectedVariant;
      this.setNewMobilePhoneVariant.emit(this.selectedVariant);
      this.changeDetectorRef.detectChanges();
    }
  }

  onVariantChanged(offering: HardwareVariant) {
    this.selectedVariant = offering;
    this.getMobileDetails();
    this.changeDetectorRef.detectChanges();
  }

  onEquipmentUpgradeNewOfferingChanged(offeringId: string) {
    if (offeringId) {
      this.newMobileForm.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.enable();
    } else {
      this.newMobileForm.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.setValue(null);
      this.newMobileForm.get(MobilePhoneFormControls.IsSubscriptionUpgrade)?.disable();
      this.newMobileForm.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.setValue(null);
      this.newMobileForm.get(MobilePhoneFormControls.SubscriptionIdToUpgrade)?.disable();
    }
    this.newMobileForm.updateValueAndValidity();
  }

  onGetExistingVoiceProductsDetails() {
    this.store.dispatch(new ApiGetAllSubscriptionOfferingsAction());
  }

  onSubmit() {
    if (this.newMobileForm.valid) {
      this.selectionCompleted.emit();
    }
  }
}
